import type { MeetingInsightThresholds } from './meetingInsightsConfig'
import type { AssetOpportunityRow, InsightsDashboardData, ReferralRow } from '../../../../../../shared/insights'

export interface VisibleMeetingInsightRows {
  topTopics: string[]
  topOptimisticAssetClass?: string
  heldAway?: { agreedPercent: number }
  referralAsk?: { conversionRate: number }
}

const evaluateReferralAsk = ({ rows, config }: { rows: ReferralRow[]; config: MeetingInsightThresholds['referralAsk'] }) => {
  let asked = 0
  let provided = 0

  for (const row of rows) {
    asked += row.referralsAskedForRate
    provided += row.referralsProvidedForRate
  }

  if (asked < config.minAsks) {
    return
  }

  const conversionRate = Math.round((provided / asked) * 100)
  if (conversionRate < config.minConversionRate) {
    return
  }

  return { conversionRate }
}

const evaluateHeldAway = ({ rows, config }: { rows: AssetOpportunityRow[]; config: MeetingInsightThresholds['heldAway'] }) => {
  let mentioned = 0
  let suggested = 0
  let agreed = 0

  for (const row of rows) {
    mentioned += row.assetMentions
    suggested += row.effectiveSuggestedForRate
    agreed += row.effectiveAgreedForRate
  }

  if (mentioned < config.minMentions) {
    return
  }

  const agreedPercent = suggested > 0 ? Math.round((agreed / suggested) * 100) : 0
  if (agreedPercent < config.minAgreementRate) {
    return
  }

  return { agreedPercent }
}

export const getVisibleMeetingInsightRows = ({
  data,
  config,
}: {
  data: InsightsDashboardData
  config: MeetingInsightThresholds
}): VisibleMeetingInsightRows => {
  const topTopics = data.discussionTopics.slice(0, 2).map((t) => t.topic)
  const topOptimisticAssetClass = data.clientOutlook?.optimisticRows[0]?.assetClass
  const heldAway = evaluateHeldAway({ rows: data.assetOpportunities?.rows ?? [], config: config.heldAway })
  const referralAsk = evaluateReferralAsk({ rows: data.referrals?.rows ?? [], config: config.referralAsk })

  return { topTopics, topOptimisticAssetClass, heldAway, referralAsk }
}
